import { formatDistanceToNow } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";

const LeadsTable = ({
  leads,
  loading,
  onView,
  onReply,
  onDelete,
  onStatusChange,
  getStatusStyle,
}) => {
  return (
    <div className="bg-white rounded-2xl lg:rounded-[24px] border border-gray-200 shadow-sm overflow-hidden min-h-[320px] lg:min-h-[500px] flex flex-col">
      {/* Mobile cards */}
      <div className="md:hidden flex-1 divide-y divide-slate-100">
        {loading ? (
          [...Array(4)].map((_, i) => (
            <div key={`m-loading-${i}`} className="p-4 animate-pulse">
              <div className="h-20 bg-slate-100 rounded-2xl" />
            </div>
          ))
        ) : leads.length === 0 ? (
          <div className="py-20 text-center">
            <div className="size-14 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3 text-slate-300">
              <span className="material-symbols-outlined text-2xl">inbox</span>
            </div>
            <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">
              No Leads Yet
            </p>
          </div>
        ) : (
          leads.map((lead) => (
            <div
              key={lead.id}
              className="p-4 active:bg-slate-50"
              onClick={() => onView(lead)}
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-slate-800 text-white flex items-center justify-center text-xs font-black shrink-0">
                  {lead.sender?.charAt(0).toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-700 truncate">
                        {lead.sender}
                      </p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {lead.companyName !== "N/A"
                          ? lead.companyName
                          : "Independent"}
                      </p>
                    </div>
                    <select
                      value={lead.status || "pending"}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => onStatusChange(lead.id, e.target.value)}
                      className={`shrink-0 px-2.5 py-1 rounded-full text-[9px] font-black uppercase outline-none border-0 ${getStatusStyle(lead.status)}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="contacted">Contacted</option>
                      <option value="converted">Converted</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="px-2 py-0.5 rounded-lg text-[9px] font-black uppercase bg-blue-50 text-blue-600">
                      {lead.serviceRequested}
                    </span>
                    <span className="text-[10px] font-bold text-slate-500 flex items-center gap-0.5">
                      <span className="material-symbols-outlined text-[12px] text-[#E63946]">
                        currency_rupee
                      </span>
                      {lead.budget}
                    </span>
                  </div>
                  <div
                    className="mt-3 flex items-center justify-between"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="text-[10px] text-slate-400 truncate">
                      {lead.email}
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => onView(lead)}
                        className="w-9 h-9 flex items-center justify-center text-slate-400 hover:bg-[#0B1C2D] hover:text-white rounded-xl active:scale-90"
                      >
                        <span className="material-symbols-outlined text-[18px]">
                          visibility
                        </span>
                      </button>
                      <button
                        onClick={() => onReply(lead)}
                        className="w-9 h-9 flex items-center justify-center text-slate-400 hover:bg-[#0B1C2D] hover:text-white rounded-xl active:scale-90"
                      >
                        <span className="material-symbols-outlined text-[18px]">
                          reply
                        </span>
                      </button>
                      <button
                        onClick={() => onDelete(lead.id)}
                        className="w-9 h-9 flex items-center justify-center text-slate-400 hover:bg-red-500 hover:text-white rounded-xl active:scale-90"
                      >
                        <span className="material-symbols-outlined text-[18px]">
                          delete
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto flex-1">
        <table className="w-full text-sm text-left border-collapse min-w-[900px]">
          <thead className="bg-gray-50/50 text-[10px] font-black uppercase tracking-widest text-gray-400 border-b border-gray-100">
            <tr>
              <th className="px-6 py-5">Prospect Details</th>
              <th className="px-6 py-5">Contact Info</th>
              <th className="px-6 py-5">Project Scope & Budget</th>
              <th className="px-6 py-5">Status</th>
              <th className="px-6 py-5 text-right">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-50">
            <AnimatePresence>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={`loading-${i}`} className="animate-pulse">
                    <td colSpan="5" className="px-6 py-6 bg-gray-50/20" />
                  </tr>
                ))
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-20 text-center">
                    <div className="size-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                      <span className="material-symbols-outlined text-3xl">
                        inbox
                      </span>
                    </div>
                    <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">
                      No Leads Yet
                    </p>
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <motion.tr
                    key={lead.id}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20, transition: { duration: 0.2 } }}
                    className="group transition-all hover:bg-slate-50 border-l-4 border-l-transparent"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-slate-800 text-white flex items-center justify-center text-xs font-black">
                          {lead.sender?.charAt(0).toUpperCase() || "?"}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-slate-600 tracking-tight">
                            {lead.sender}
                          </span>
                          <span className="text-[11px] font-medium text-slate-400 uppercase tracking-widest flex items-center gap-1 mt-0.5">
                            <span className="material-symbols-outlined text-[12px]">
                              business
                            </span>
                            {lead.companyName !== "N/A"
                              ? lead.companyName
                              : "Independent"}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className="text-[11px] font-bold text-slate-500 flex items-center gap-2">
                          <span className="material-symbols-outlined text-[14px] text-slate-400">
                            mail
                          </span>
                          {lead.email}
                        </span>
                        <span className="text-[11px] font-bold text-slate-500 flex items-center gap-2 mt-1">
                          <span className="material-symbols-outlined text-[14px] text-slate-400">
                            call
                          </span>
                          {lead.phone || "Not Provided"}
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex flex-col items-start gap-1">
                        <span className="px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-widest bg-blue-50 text-blue-600">
                          {lead.serviceRequested}
                        </span>
                        <span className="text-[11px] font-bold text-slate-500 uppercase flex items-center gap-1 mt-1">
                          <span className="material-symbols-outlined text-[12px] text-[#E63946]">
                            currency_rupee
                          </span>
                          Budget: {lead.budget}
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex flex-col items-start gap-1">
                        <select
                          value={lead.status || "pending"}
                          onChange={(e) =>
                            onStatusChange(lead.id, e.target.value)
                          }
                          className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter outline-none cursor-pointer border-0 appearance-none text-center hover:opacity-80 transition-opacity ${getStatusStyle(lead.status)}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="contacted">Contacted</option>
                          <option value="converted">Converted</option>
                          <option value="rejected">Rejected</option>
                        </select>
                        <span className="text-[9px] font-medium text-slate-300 mt-1">
                          {lead.createdAt
                            ? formatDistanceToNow(new Date(lead.createdAt), {
                                addSuffix: true,
                              })
                            : "Recently"}
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => onView(lead)}
                          className="w-9 h-9 flex items-center justify-center text-slate-400 hover:bg-[#0B1C2D] hover:text-white rounded-xl transition-all"
                          title="View Full Details"
                        >
                          <span className="material-symbols-outlined text-[18px]">
                            visibility
                          </span>
                        </button>
                        <button
                          onClick={() => onReply(lead)}
                          className="w-9 h-9 flex items-center justify-center text-slate-400 hover:bg-[#0B1C2D] hover:text-white rounded-xl transition-all"
                          title="Reply via Email"
                        >
                          <span className="material-symbols-outlined text-[18px]">
                            reply
                          </span>
                        </button>
                        <button
                          onClick={() => onDelete(lead.id)}
                          className="w-9 h-9 flex items-center justify-center text-slate-400 hover:bg-red-500 hover:text-white rounded-xl transition-all"
                          title="Delete Lead"
                        >
                          <span className="material-symbols-outlined text-[18px]">
                            delete
                          </span>
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeadsTable;
